// AdminNavigation.jsx - Menu d'administration mis à jour
import React, { useState, useEffect } from 'react';
import {
  LayoutDashboard, Video, Shield, Eye, Settings, LogOut, 
  Menu, X, ChevronLeft, ChevronRight, Sparkles, Globe,
  Users, BarChart3, Youtube, Plus, Search, TrendingUp,
  Bell, Home, Cpu, MapPin, Mic, CreditCard, Database,
  Activity, Zap, Star, Crown, Coffee, Diamond, FileText, Brain
} from 'lucide-react';

import { useAdminAuth } from './AdminAuthContext';
import videoServices from '../CP-Academy/VideoService';

// Style pour cacher la scrollbar
const scrollbarStyles = `
  .scrollbar-hide {
    -ms-overflow-style: none;
    scrollbar-width: none;
  }
  .scrollbar-hide::-webkit-scrollbar {
    display: none;
  }
`;

const AdminNavigation = ({ 
  currentPage, 
  onNavigate,
  className = ""
}) => {
  const [menuCollapsed, setMenuCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [showStatsModal, setShowStatsModal] = useState(false);
  const [adminStats, setAdminStats] = useState(null);
  const { logout } = useAdminAuth();

  // Profil administrateur
  const adminProfile = {
    name: "Administrateur",
    role: "Super Admin",
    permissions: "Tous droits",
    lastLogin: new Date().toLocaleDateString()
  };

  // Structure du menu d'administration MISE À JOUR
  const adminMenuStructure = {
    main: [
      { 
        icon: LayoutDashboard, 
        label: 'Tableau de bord', 
        id: 'dashboard',
        description: 'Vue d\'ensemble et statistiques'
      },
      { 
        icon: Search, 
        label: 'Découverte IA', 
        id: 'discovery',
        description: 'Recherche et curation de formations',
        badge: 'IA',
        badgeColor: 'bg-purple-500'
      },
      { 
        icon: FileText, 
        label: 'Gestion Vidéos', 
        id: 'videos',
        description: 'Administration de la bibliothèque',
        badge: 'Nouveau',
        badgeColor: 'bg-green-500'
      }
    ],
    academy: [
      { 
        icon: Shield, 
        label: 'Academy (Mode Admin)', 
        id: 'academy-admin',
        description: 'Interface avec outils admin'
      },
      { 
        icon: Eye, 
        label: 'Aperçu Public', 
        id: 'academy-public',
        description: 'Vue utilisateur final'
      }
    ],
    system: [
      { 
        icon: Database, 
        label: 'Base de données', 
        id: 'database',
        description: 'Gestion des données'
      },
      { 
        icon: Users, 
        label: 'Utilisateurs', 
        id: 'users',
        description: 'Gestion des comptes'
      }
    ]
  };

  // Charger les statistiques admin
  useEffect(() => {
    loadAdminStats();
  }, []);

  const loadAdminStats = async () => {
    try {
      const allVideos = await videoServices.getAllVideos();
      
      // Calculer les vraies statistiques
      const totalVideos = allVideos.length;
      const totalViews = allVideos.reduce((sum, v) => sum + (v.viewCount || 0), 0);
      const recentlyAdded = allVideos.filter(v => {
        const weekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
        return new Date(v.addedAt) > weekAgo;
      }).length;
      
      // Compter les thèmes actifs
      const themes = new Set(allVideos.map(v => v.theme));
      const activeThemes = themes.size;
      
      setAdminStats({
        totalVideos,
        totalViews,
        activeThemes,
        recentlyAdded
      });
    } catch (error) {
      console.error('Erreur chargement stats admin:', error);
      // Valeurs par défaut en cas d'erreur
      setAdminStats({
        totalVideos: 0,
        totalViews: 0,
        activeThemes: 0,
        recentlyAdded: 0
      });
    }
  };

  const handleNavigation = (pageId) => {
    onNavigate(pageId);
    setMobileMenuOpen(false);
  };

  const handleLogout = () => {
    if (window.confirm('Êtes-vous sûr de vouloir vous déconnecter ?')) {
      logout();
    }
  };

  const toggleMenu = () => {
    setMenuCollapsed(!menuCollapsed);
  };

  // Modal des statistiques système
  const StatsModal = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Statistiques système</h2>
              <p className="text-gray-600 mt-1">Vue d'ensemble de la plateforme Aladinn Academy</p>
            </div>
            <button 
              onClick={() => setShowStatsModal(false)}
              className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center hover:bg-gray-200"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <div className="bg-blue-50 p-6 rounded-xl border border-blue-200">
              <div className="flex items-center gap-4">
                <div className="bg-blue-600 p-3 rounded-lg">
                  <Video className="w-6 h-6 text-white" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-blue-900">{adminStats?.totalVideos || 0}</div>
                  <div className="text-sm text-blue-700">Total vidéos</div>
                </div>
              </div>
            </div>
            
            <div className="bg-green-50 p-6 rounded-xl border border-green-200">
              <div className="flex items-center gap-4">
                <div className="bg-green-600 p-3 rounded-lg">
                  <TrendingUp className="w-6 h-6 text-white" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-green-900">{adminStats?.recentlyAdded || 0}</div>
                  <div className="text-sm text-green-700">Cette semaine</div>
                </div>
              </div>
            </div>
            
            <div className="bg-purple-50 p-6 rounded-xl border border-purple-200">
              <div className="flex items-center gap-4">
                <div className="bg-purple-600 p-3 rounded-lg">
                  <Eye className="w-6 h-6 text-white" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-purple-900">
                    {adminStats ? (adminStats.totalViews > 1000 ? `${(adminStats.totalViews / 1000).toFixed(1)}k` : adminStats.totalViews) : '0'}
                  </div>
                  <div className="text-sm text-purple-700">Vues totales</div>
                </div>
              </div>
            </div>
            
            <div className="bg-orange-50 p-6 rounded-xl border border-orange-200">
              <div className="flex items-center gap-4">
                <div className="bg-orange-600 p-3 rounded-lg">
                  <BarChart3 className="w-6 h-6 text-white" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-orange-900">{adminStats?.activeThemes || 0}</div>
                  <div className="text-sm text-orange-700">Thèmes actifs</div>
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-gray-50 p-6 rounded-xl">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">État du système</h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Base de données Firebase</span>
                  <span className="text-green-600 text-sm font-medium">✅ Opérationnelle</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Backend IA</span>
                  <span className="text-green-600 text-sm font-medium">✅ Fonctionnel</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Système de cache</span>
                  <span className="text-green-600 text-sm font-medium">✅ Actif</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Authentification</span>
                  <span className="text-green-600 text-sm font-medium">✅ Sécurisée</span>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 p-6 rounded-xl">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Actions rapides</h3>
              <div className="space-y-3">
                <button
                  onClick={() => {
                    setShowStatsModal(false);
                    handleNavigation('discovery');
                  }}
                  className="w-full text-left p-3 rounded-lg border border-gray-200 hover:bg-white transition-colors"
                >
                  <div className="font-medium text-gray-900">Découverte IA</div>
                  <div className="text-sm text-gray-600">Rechercher de nouvelles formations</div>
                </button>
                
                <button
                  onClick={() => {
                    setShowStatsModal(false);
                    handleNavigation('videos');
                  }}
                  className="w-full text-left p-3 rounded-lg border border-gray-200 hover:bg-white transition-colors"
                >
                  <div className="font-medium text-gray-900">Gestion Vidéos</div>
                  <div className="text-sm text-gray-600">Administrer la bibliothèque</div>
                </button>
                
                <button
                  onClick={() => {
                    setShowStatsModal(false);
                    handleNavigation('academy-admin');
                  }}
                  className="w-full text-left p-3 rounded-lg border border-gray-200 hover:bg-white transition-colors"
                >
                  <div className="font-medium text-gray-900">Mode Admin Academy</div>
                  <div className="text-sm text-gray-600">Interface avec outils admin</div>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // Modal des paramètres administrateur
  const SettingsModal = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Paramètres d'administration</h2>
              <p className="text-gray-600 mt-1">Configuration système et préférences</p>
            </div>
            <button 
              onClick={() => setShowSettingsModal(false)}
              className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center hover:bg-gray-200"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {/* Paramètres IA */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Intelligence Artificielle</h3>
            <div className="grid grid-cols-1 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Brain className="w-4 h-4 inline mr-2" />
                  Niveau de confiance IA minimum
                </label>
                <select className="w-full border border-gray-300 rounded-lg px-3 py-2">
                  <option>70% (Recommandé)</option>
                  <option>60% (Plus permissif)</option>
                  <option>80% (Plus strict)</option>
                  <option>90% (Très strict)</option>
                </select>
              </div>
            </div>
          </div>

          {/* Paramètres de contenu */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Gestion du contenu</h3>
            <div className="space-y-4">
              <label className="flex items-center">
                <input type="checkbox" defaultChecked className="mr-3" />
                <span className="text-sm text-gray-700">Modération automatique des nouvelles vidéos</span>
              </label>
              <label className="flex items-center">
                <input type="checkbox" defaultChecked className="mr-3" />
                <span className="text-sm text-gray-700">Notifications des ajouts de contenu</span>
              </label>
              <label className="flex items-center">
                <input type="checkbox" className="mr-3" />
                <span className="text-sm text-gray-700">Sauvegarde automatique quotidienne</span>
              </label>
              <label className="flex items-center">
                <input type="checkbox" defaultChecked className="mr-3" />
                <span className="text-sm text-gray-700">Analyse IA des critères Aladinn</span>
              </label>
            </div>
          </div>

          {/* Sécurité */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Sécurité</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Durée de session admin
                </label>
                <select className="w-full border border-gray-300 rounded-lg px-3 py-2">
                  <option>2 heures</option>
                  <option>4 heures</option>
                  <option>8 heures (Recommandé)</option>
                  <option>24 heures</option>
                </select>
              </div>
              <label className="flex items-center">
                <input type="checkbox" defaultChecked className="mr-3" />
                <span className="text-sm text-gray-700">Journalisation des actions admin</span>
              </label>
              <label className="flex items-center">
                <input type="checkbox" defaultChecked className="mr-3" />
                <span className="text-sm text-gray-700">Authentification à double facteur</span>
              </label>
            </div>
          </div>
        </div>

        <div className="p-6 border-t bg-gray-50 flex justify-end space-x-3">
          <button 
            onClick={() => setShowSettingsModal(false)}
            className="px-4 py-2 text-gray-600 hover:text-gray-800"
          >
            Annuler
          </button>
          <button 
            onClick={() => setShowSettingsModal(false)}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Enregistrer
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <>
      {/* CSS pour cacher les scrollbars */}
      <style>{scrollbarStyles}</style>
      
      {/* Modals */}
      {showStatsModal && <StatsModal />}
      {showSettingsModal && <SettingsModal />}

      {/* Bouton mobile */}
      <button
        className="lg:hidden fixed top-4 left-4 z-50 p-2 bg-white rounded-lg shadow-lg hover:bg-gray-100"
        onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
      >
        {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
      </button>

      {/* Overlay mobile */}
      {mobileMenuOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-40"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Menu principal */}
      <div 
        className={`fixed top-0 left-0 h-screen bg-gradient-to-br from-gray-800 to-gray-900 text-white shadow-xl z-40
                   transition-all duration-300 ease-in-out overflow-hidden
                   ${menuCollapsed ? 'w-16' : 'w-72'}
                   ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
                   ${className}`}
      >
        {/* En-tête */}
        <div className="h-16 border-b border-white/10 flex items-center justify-between px-4">
          <div className={`flex items-center ${menuCollapsed ? 'justify-center w-full' : ''}`}>
            <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
              <Shield className="w-5 h-5" />
            </div>
            {!menuCollapsed && (
              <div className="ml-3">
                <span className="font-bold text-xl">Admin Aladinn</span>
                <div className="text-xs text-blue-200">Interface d'administration</div>
              </div>
            )}
          </div>
          
          <button
            onClick={toggleMenu}
            className="hidden lg:flex items-center justify-center w-8 h-8 rounded-full hover:bg-white/10"
          >
            {menuCollapsed ? (
              <ChevronRight className="w-5 h-5" />
            ) : (
              <ChevronLeft className="w-5 h-5" />
            )}
          </button>
        </div>

        <div className="h-[calc(100vh-4rem)] overflow-y-auto scrollbar-hide">
          {/* Profil administrateur */}
          {!menuCollapsed && (
            <div className="p-4 border-b border-white/10">
              <div className="text-center">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-600 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-3">
                  <span className="text-white text-lg font-bold">A</span>
                </div>
                <h3 className="font-semibold">{adminProfile.name}</h3>
                <p className="text-sm text-gray-300">{adminProfile.role}</p>
              </div>
            </div>
          )}

          {/* Navigation principale */}
          <div className="p-3 pb-20">
            
            {/* Section principale */}
            <div className="mb-6">
              {!menuCollapsed && (
                <h4 className="text-xs font-semibold text-gray-300 uppercase tracking-wide mb-3 px-2">
                  Administration
                </h4>
              )}
              <div className="space-y-2">
                {adminMenuStructure.main.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => handleNavigation(item.id)}
                    className={`w-full flex items-center p-3 rounded-xl transition-all duration-200 relative
                      ${currentPage === item.id 
                        ? 'bg-white/20 border border-white/20' 
                        : 'hover:bg-white/10'}`}
                  >
                    <div className={`p-2 rounded-lg ${currentPage === item.id ? 'bg-blue-600/30' : 'bg-white/10'}`}>
                      <item.icon className="w-5 h-5" />
                    </div>
                    {!menuCollapsed && (
                      <div className="ml-3 flex-1 text-left">
                        <div className="font-medium flex items-center gap-2">
                          {item.label}
                          {item.badge && (
                            <span className={`${item.badgeColor} text-white text-xs px-2 py-0.5 rounded-full font-medium`}>
                              {item.badge}
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-gray-300 mt-0.5">{item.description}</div>
                      </div>
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* Section Academy */}
            <div className="mb-6">
              {!menuCollapsed && (
                <h4 className="text-xs font-semibold text-gray-300 uppercase tracking-wide mb-3 px-2">
                  Academy
                </h4>
              )}
              <div className="space-y-2">
                {adminMenuStructure.academy.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => handleNavigation(item.id)}
                    className={`w-full flex items-center p-3 rounded-xl transition-all duration-200
                      ${currentPage === item.id 
                        ? 'bg-white/20 border border-white/20' 
                        : 'hover:bg-white/10'}`}
                  >
                    <div className={`p-2 rounded-lg ${currentPage === item.id ? 'bg-green-600/30' : 'bg-white/10'}`}>
                      <item.icon className="w-5 h-5" />
                    </div>
                    {!menuCollapsed && (
                      <div className="ml-3 flex-1 text-left">
                        <div className="font-medium text-sm">{item.label}</div>
                        <div className="text-xs text-gray-300 mt-0.5 leading-tight">{item.description}</div>
                      </div>
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* Section système */}
            {!menuCollapsed && (
              <div className="mb-6">
                <h4 className="text-xs font-semibold text-gray-300 uppercase tracking-wide mb-3 px-2">
                  Système
                </h4>
                <div className="space-y-2">
                  {adminMenuStructure.system.map((item) => (
                    <button
                      key={item.id}
                      onClick={() => handleNavigation(item.id)}
                      className={`w-full flex items-center p-3 rounded-xl transition-all duration-200
                        ${currentPage === item.id 
                          ? 'bg-white/20 border border-white/20' 
                          : 'hover:bg-white/10'}`}
                    >
                      <div className={`p-2 rounded-lg ${currentPage === item.id ? 'bg-purple-600/30' : 'bg-white/10'}`}>
                        <item.icon className="w-5 h-5" />
                      </div>
                      <div className="ml-3 flex-1 text-left">
                        <div className="font-medium text-sm">{item.label}</div>
                        <div className="text-xs text-gray-300 mt-0.5">{item.description}</div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Actions du bas */}
          <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/20 border-t border-white/10">
            {!menuCollapsed ? (
              <div className="space-y-2">
                <button 
                  onClick={() => setShowStatsModal(true)}
                  className="w-full flex items-center p-2 rounded-lg hover:bg-white/10 transition-colors"
                >
                  <Activity className="w-4 h-4 mr-3" />
                  <span className="text-sm">Statistiques système</span>
                </button>
                
                <a
                  href="/academy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full flex items-center p-2 rounded-lg hover:bg-white/10 transition-colors"
                >
                  <Globe className="w-4 h-4 mr-3" />
                  <span className="text-sm">Site public</span>
                </a>
                
                <button 
                  onClick={() => setShowSettingsModal(true)}
                  className="w-full flex items-center p-2 rounded-lg hover:bg-white/10 transition-colors"
                >
                  <Settings className="w-4 h-4 mr-3" />
                  <span className="text-sm">Paramètres</span>
                </button>
                
                <button 
                  onClick={handleLogout}
                  className="w-full flex items-center p-2 rounded-lg text-red-300 hover:bg-red-600/20 transition-colors"
                >
                  <LogOut className="w-4 h-4 mr-3" />
                  <span className="text-sm">Déconnexion</span>
                </button>
              </div>
            ) : (
              <div className="space-y-2">
                <button 
                  onClick={() => setShowStatsModal(true)}
                  className="w-full flex items-center justify-center p-2 rounded-lg hover:bg-white/10 transition-colors"
                  title="Statistiques"
                >
                  <Activity className="w-4 h-4" />
                </button>
                <button 
                  onClick={() => setShowSettingsModal(true)}
                  className="w-full flex items-center justify-center p-2 rounded-lg hover:bg-white/10 transition-colors"
                  title="Paramètres"
                >
                  <Settings className="w-4 h-4" />
                </button>
                <button 
                  onClick={handleLogout}
                  className="w-full flex items-center justify-center p-2 rounded-lg text-red-300 hover:bg-red-600/20 transition-colors"
                  title="Déconnexion"
                >
                  <LogOut className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default AdminNavigation;